import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter } from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { unifiedContactService } from '../../services/unifiedContactService';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { assetAggregationService, type UnifiedAsset } from '../../services/assetAggregationService.ts';
import { API_BASE_URL } from '../../config/api';
import { ENABLE_BENEFICIARY_PORTAL } from '../../config/planAccessConfig';

interface SelectedBeneficiary {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

interface DistributionRecord {
  assetId: string;
  primaryBeneficiaries?: { beneficiaryId: string; percentage?: number }[];
  contingentBeneficiaries?: { beneficiaryId: string; percentage?: number }[];
  instructions?: string;
  notes?: string;
}

interface BeneficiaryWithSummary {
  contact: UnifiedContact;
  distributionCategories: string;
}

const Frame303: React.FC = () => {
  const navigate = useNavigate();

  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { session, updateTaskProgress, completeTask } = useSessionPersistence();

  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<SelectedBeneficiary[]>([]);
  const [assets, setAssets] = useState<UnifiedAsset[]>([]);
  const [distributions, setDistributions] = useState<Record<string, DistributionRecord>>({});

  const [isSending, setIsSending] = useState(false);
  const [beneficiaryPersonalMessages, setBeneficiaryPersonalMessages] = useState<Record<string, string>>({});
  const hasHydratedFromSession = useRef(false);

  // Load contacts and assets
  useEffect(() => {
    if (session) {
        const loadData = async () => {
            setIsLoading(true);
            try {
              // Load contacts from unified service
              const allContacts = await unifiedContactService.getAllContacts();
              setContacts(allContacts);

              // Load previously selected beneficiaries from session
              const savedDistributions = session.taskProgress?.assetDistribution?.answers?.distributions || {};
              
              // Convert the saved distributions to a record of assetId to DistributionRecord
              if (Array.isArray(savedDistributions)) {
                const map: Record<string, DistributionRecord> = {};
                savedDistributions.forEach((d: unknown) => {
                  if (d.assetId) map[d.assetId] = d;
                });
                setDistributions(map);
              } else {
                setDistributions(savedDistributions);
              }

              // Load all assets
              const unified = assetAggregationService.getAllAssets(session);
              setAssets(unified);
            } catch (err) {
                console.error('[Frame303] Error loading data:', err);
                setError('Failed to load contacts');
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }
    else {
        setIsLoading(false);
        setError('No session data found');
    }
  }, [session]);


  // Load saved beneficiaries from session
  useEffect(() => {
    const saved = session?.taskProgress?.invite_beneficiaries?.answers;
    if (!saved || hasHydratedFromSession.current) return;
    hasHydratedFromSession.current = true;
    if (saved.selectedBeneficiaries?.length) {
      setSelectedBeneficiaries(saved.selectedBeneficiaries);
    }
    if (saved.beneficiaryPersonalMessages) {
      setBeneficiaryPersonalMessages(saved.beneficiaryPersonalMessages);
    }
  }, [session?.taskProgress?.invite_beneficiaries?.answers]);



  const toggleBeneficiary = (contact: UnifiedContact) => {
    const isSelected = selectedBeneficiaries.some(e => e.id === contact.id);

    if (isSelected) {
      setSelectedBeneficiaries(prev => prev.filter(e => e.id !== contact.id));
    } else {
      if (!contact.email) {
        alert('This contact needs an email address to be invited as a beneficiary.');
        return;
      }
      setSelectedBeneficiaries(prev => [...prev, {
        id: contact.id,
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email
      }]);
    }
  };


  const beneficiariesWithCategories = useMemo((): BeneficiaryWithSummary[] => {
    const distList = Object.values(distributions); // list of all distribution records
    if (distList.length === 0 || contacts.length === 0 || assets.length === 0) return [];
    
    const beneficiaryIds = [...new Set(// Get all unique beneficiary IDs using set to avoid duplicates
      distList.flatMap((d: unknown) => [// Flat map so we can get all beneficiary IDs
        ...(d.primaryBeneficiaries || []).map((p: unknown) => p.beneficiaryId),
        ...(d.contingentBeneficiaries || []).map((c: unknown) => c.beneficiaryId)
      ].filter(Boolean)
      )
    )];

    const assetById = new Map(assets.map(a => [a.id, a])); // get a list of all assets by id
  
    // Return all beneficiaries with their assigned assets and categories
    return beneficiaryIds.map(beneficiaryId => {
      // Get the contact for this beneficiary
      const contact = contacts.find(c => c.id === beneficiaryId);
      if (!contact) return null;

      // Get all asset ids assigned to this beneficiary
      const assignedAssetIds = distList
        .filter((d: unknown) => 
          (d.primaryBeneficiaries || []).some((p: unknown) => p.beneficiaryId === beneficiaryId) // Check if the beneficiary ID is in the primary beneficiaries array for this distribution record
      ).map((d: unknown) => d.assetId); // Get the asset IDs for this distribution record

      // Count the number of assets assigned to this beneficiary by category
      const categoryCounts: Record<string, number> = {};
      for (const assetId of assignedAssetIds) {
        const asset = assetById.get(assetId);
        if (asset?.category) {
          categoryCounts[asset.category] = (categoryCounts[asset.category] ?? 0) + 1;
        }
      }

      // Format the category summary
      const categorySummary = Object.entries(categoryCounts)
          .sort(([a], [b]) => a.localeCompare(b)) // sort categories alphabetically
          .map(([cat, count]) => `${count} ${cat}`) // Format the category summary as "count category"
          .join(', ') || '—'; // If no categories, use "—"

      // Return the beneficiary with the category summary
      return {
        contact,
        distributionCategories: categorySummary
      };
    })
    .filter((b): b is BeneficiaryWithSummary => b !== null); // Remove any null beneficiaries
  }, [distributions, contacts, assets]);

  const handleNext = async () => {

    if (selectedBeneficiaries.length === 0) {
      await updateTaskProgress('invite_beneficiaries', {selectedBeneficiaries: [], beneficiaryPersonalMessages: {}});
      navigate('/frame-304');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await updateTaskProgress('invite_beneficiaries', {selectedBeneficiaries: selectedBeneficiaries, beneficiaryPersonalMessages: beneficiaryPersonalMessages});
      
      const token = localStorage.getItem('account-token');

      const response = await fetch(`${API_BASE_URL}/api/beneficiaries/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          beneficiaries: selectedBeneficiaries.map((b) => ({
            email: b.email,
            name: `${b.firstName} ${b.lastName}`.trim(),
            ...(beneficiaryPersonalMessages[b.id]?.trim() && {
              personalMessage: beneficiaryPersonalMessages[b.id].trim(),
            }),
          })),
        }),
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send beneficiary invites');
      }
  
      await response.json();
      completeTask('invite_beneficiaries');
      
      navigate('/frame-304');
    } catch (error) {
      console.error('[Frame303] Error saving beneficiaries:', error);
      setError('Failed to send beneficiary invites. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleBack = () => {
    updateTaskProgress('invite_beneficiaries', {
      selectedBeneficiaries,
      beneficiaryPersonalMessages,
    });
    navigate('/frame-302');
  };

  const getPersonName = (contact: UnifiedContact) => {
    return `${contact.firstName} ${contact.lastName}`.trim();
  };

  // Redirect to frame-304 if beneficiary portal is not enabled
  if (!ENABLE_BENEFICIARY_PORTAL) {
    return <Navigate to="/frame-304" replace />;
  }

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader title="Invite beneficiaries" />

        <div className="max-w-4xl space-y-6">
          {/* Description */}
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            Inviting beneficiaries gives them limited, read-only visibility into your planning intentions. This can help set context and reduce surprises later, without creating legal rights or commitments.
            You’re in control. You can choose who to invite, skip this step, or update access at any time.
            </p>

          </div>

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          )}

          {/* Contact Selection */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-cyan-700 border-t-transparent rounded-full animate-spin"></div>
              <p className="text-stone-600 mt-4">Loading contacts...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Selected beneficiaries summary */}
              {selectedBeneficiaries.length > 0 && (
                <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                  <h3 className="font-semibold text-cyan-900 mb-2">
                  {selectedBeneficiaries.length !== 1 ? 'Selected Beneficiaries' : 'Selected Beneficiary'} ({selectedBeneficiaries.length})
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBeneficiaries.map(beneficiary => (
                      <span
                        key={beneficiary.id}
                        className="inline-flex items-center gap-2 px-3 py-1 bg-white rounded-full text-sm text-cyan-800 border border-cyan-300"
                      >
                        {beneficiary.firstName} {beneficiary.lastName}
                        <button
                          onClick={() => setSelectedBeneficiaries(prev => prev.filter(e => e.id !== beneficiary.id))}
                          className="text-cyan-600 hover:text-cyan-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact list */}
              <div className="bg-white rounded-lg border border-stone-200">
                <div className="p-4 border-b border-stone-200 flex justify-between items-center">
                  <h3 className="font-semibold text-stone-800">Your Contacts</h3> 
                </div>


                {beneficiariesWithCategories.length === 0 ? (
                  <div className="p-8 text-center text-stone-500">
                    <p>No beneficiaries with assigned assets found.</p>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {beneficiariesWithCategories.map(({ contact, distributionCategories }) => {
                      const isSelected = selectedBeneficiaries.some(e => e.id === contact.id);
                      
                      return (
                        <div
                          key={contact.id}
                          onClick={() => toggleBeneficiary(contact)}
                          className={`p-4 border-b border-stone-100 last:border-b-0 cursor-pointer transition-colors ${
                            isSelected
                              ? 'bg-cyan-50'
                              : 'hover:bg-stone-50'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            {/* Checkbox */}
                            <div
                              className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'bg-cyan-700 border-cyan-700'
                                  : 'border-stone-300'
                              }`}
                            >
                              {isSelected && (
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>

                            {/* Contact info */}
                            <div className="flex-1">
                              <div className="font-medium text-stone-800">
                                {getPersonName(contact)}
                              </div>
                              {contact.email ? (
                                <div className="text-sm text-stone-600">{contact.email}</div>
                              ) : (
                                <div className="text-sm text-orange-600">No email address</div>
                              )}
                              {contact.relationship && (
                                <div className="text-xs text-stone-500 capitalize">{contact.relationship}</div>
                              )}
                              {distributionCategories && (
                                <div className="text-sm text-stone-600 capitalize"> Assigned assets: {distributionCategories}</div>
                              )}
                            </div>

                            
                          </div>
                          {isSelected && (
                            <div
                              className="bg-white border border-stone-200 rounded-lg p-4 mt-3"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <label className="block text-sm font-medium text-stone-700 mb-2">
                                Personal Message (Optional)
                              </label>
                              <textarea
                                value={beneficiaryPersonalMessages[contact.id] ?? ''}
                                onChange={(e) =>
                                  setBeneficiaryPersonalMessages((prev) => ({
                                    ...prev,
                                    [contact.id]: e.target.value,
                                  }))
                                }
                                placeholder="Add a personal note that will be included in the invitation email to this beneficiary..."
                                rows={4}
                                maxLength={500} // Include character limit
                                className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm text-stone-700 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                              />
                              <p className="mt-1 text-xs text-stone-500">
                                This message will be included in the email invitation sent to this beneficiary.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Confirm Plan Access"
        subtitle="Confirm Beneficiary Access"
        progressPercent={66}
        onNext={handleNext}
        onBack={handleBack}
        nextDisabled={isSending}
        nextText={selectedBeneficiaries.length > 0 ? 'Invite' : 'Skip'}
        backText="Back"
      />
    </ResponsiveContainer>
  );
};

export default Frame303;
